import { Builder, BuilderComponent } from '@builder.io/react';
import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import dynamic from 'next/dynamic';
import { useEffect } from 'react';
import Layout from '@/components/Layout/Layout';
import { getBlocks } from '@/utils/builder';
import { IS_DEV } from '@/utils/env';
import { injectLastUpdatedBlock } from '@/utils/injectLastUpdatedBlock';
import { getPromotions } from '@/services/promo.service';
import { fetchOrderStatus, isOrderStatusError } from '@/services/orderStatus.service';
import { setLocalization } from '@/utils/i18n';
import { DeviceType } from '@/utils/utils';
import useStore from '@/store/useStore';
import * as appointmentService from '@/services/appointment.service';
import NotFound from '@/components/NotFound/NotFound';
import { BuilderProvider } from '@/context/BuilderContext';
import { addDataLayerListener, clearDataLayerListener } from '@/utils/gaDataPush';
import { renderMobileNavShellHtml, renderNavHtml } from '@/widgets/navLinks/renderNavHtml';
import {
  injectOrderStatusIntoBlocks,
  isBuilderPreviewRequest,
  isOrderStatusPage,
  resolveOrderStatusCredentials,
  toPublicOrderStatus,
} from '@/utils/injectOrderStatus';

const HeaderDynamic = dynamic(() => import('@/components/Header/Header'));
const MobileHeaderDynamic = dynamic(() => import('@/components/Header/components/MobileHeader'));

export default function Page({
  page,
  data,
  locale,
  showroomsWithIds,
  device,
  navHtml = '',
  mobileNavHtml = '',
  orderStatus = null,
  orderStatusError = null,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [navLinks, mobileNavLinks, footerLinks, mobileFooterLinks, showrooms, ...builderData] = data;
  const setBuilderData = useStore(state => state.setBuilderData);
  const setMobileNavLinks = useStore(state => state.setMobileNavLinks);
  const setBuilderShowrooms = useStore(state => state.setBuilderShowrooms);
  setBuilderData(builderData);
  setLocalization(locale!);
  setBuilderShowrooms(showroomsWithIds || showrooms);

  useEffect(() => {
    setMobileNavLinks(mobileNavLinks);
  }, []);

  return (
    <BuilderProvider
      data={{
        showrooms: showroomsWithIds || showrooms || [],
        device,
        orderStatus,
        orderStatusError,
      }}
    >
      <Layout page={page} builderData={builderData} footerLinks={footerLinks} mobileFooterLinks={mobileFooterLinks}>
        <MobileHeaderDynamic
          className="tw-block md:tw-hidden"
          mobileNavLinks={mobileNavLinks}
          mobileNavHtml={mobileNavHtml}
        />
        <HeaderDynamic className="tw-hidden md:tw-block" navLinks={navLinks} navHtml={navHtml} />
        {!page && !Builder.isPreviewing ? (
          <NotFound />
        ) : (
          <BuilderComponent
            model="page"
            content={page}
            locale={locale}
            options={{ includeRefs: true }}
            contentLoaded={(_data, content) => {
              // Re-inject after Builder preview/editing replaces SSR content
              injectLastUpdatedBlock(content);
              // @ts-ignore
              if (content.testVariationId && !content?.meta?.winningTest) {
                const onOneTrustReady = pushedObj => {
                  if (pushedObj.event === 'OneTrustGroupsUpdated_Ready') {
                    (window as any).dataLayer?.push({
                      event: 'builder_ab_test',
                      test_page: content?.name,
                      test_variant: content?.id === content?.testVariationId ? 'Default' : content?.testVariationName,
                    });

                    // Builder event pushed one time only.
                    clearDataLayerListener(onOneTrustReady);
                  }
                };
                addDataLayerListener(onOneTrustReady);
              }
            }}
          />
        )}
      </Layout>
    </BuilderProvider>
  );
}

export async function getServerSideProps({ params, locale, res, req, query }: GetServerSidePropsContext) {
  const device = req?.headers['device_type'] as DeviceType;
  const formattedLocale = locale!.substring(0, 2) + locale!.substring(2, 5).toUpperCase();
  const formattedUrl = Array.isArray(params?.page) ? `/${params?.page.join('/')}` : '/';
  const utmSource = query?.utm_source;
  const utmMedium = query?.utm_medium;
  const utmCampaign = query?.utm_campaign;
  try {
    const navLink = 'nav-links';

    /* --- BUILDER --- */
    const userAttributes = {
      urlPath: formattedUrl || null,
      locale: formattedLocale || null,
      device,
      utmSource,
      utmMedium,
      utmCampaign,
    };
    const builderReq = getBlocks([
      {
        modelName: 'page',
        userAttributes,
        options: {
          url: formattedUrl,
          locale: formattedLocale,
        },
      },
      { modelName: navLink, all: true },
      { modelName: 'mobile-nav-links', all: true },
      { modelName: 'footer-links-desktop', all: true },
      { modelName: 'footer-links-mobile', all: true },
      {
        modelName: 'showrooms',
        all: true,
        options: {
          limit: 60,
        },
      },
      {
        modelName: 'header-banner',
        userAttributes,
        options: {
          locale: formattedLocale,
        },
      },
      getPromotions,
      { modelName: 'page-json', all: true },
    ]);

    /* --- SETSTER --- */
    const citiesReq = appointmentService.getCitiesData(IS_DEV);

    /* --- REQUESTS --- */
    const [builderRes, citiesRes] = await Promise.allSettled([builderReq, citiesReq]);

    let page;
    let data;
    if (builderRes.status === 'fulfilled') {
      [page, ...data] = builderRes.value;
    } else {
      throw new Error(builderRes.reason);
    }
    if (!page || page.data.url === '/error-page/') {
      res.statusCode = 404;
    }
    const showrooms = data[4];
    let showroomsWithIds;
    if (citiesRes.status === 'fulfilled') {
      const { data: cities } = citiesRes.value;

      const showroomsWithCityId = showrooms.map(showroom => {
        const city = cities?.find(c => c.name?.trim() === showroom.setsterLocationId?.trim());
        return {
          ...showroom,
          id: city?.id ?? null,
          timezoneId: city?.['timezone_id'] ?? null,
          timezoneAbreviation: city?.timezone.abreviation ?? null,
        };
      });

      showroomsWithIds = showroomsWithCityId;
    } else {
      // Cities request rejected
    }

    let orderStatusProp: any = null;
    let orderStatusErrorProp: any = null;
    if (page?.data) {
      let blocks = (page.data.blocks || []).map(block => {
        if (block.linkUrl && block.component.options.imgAlt) {
          return {
            ...block,
            properties: {
              ...block.properties,
              'aria-label': block.component.options.imgAlt,
            },
          };
        }
        return block;
      });

      if (isOrderStatusPage(formattedUrl)) {
        const { orderNumber, token, email } = resolveOrderStatusCredentials(query as Record<string, unknown>, blocks);

        if (!orderNumber || (!token && !email)) {
          orderStatusErrorProp = isBuilderPreviewRequest(query as Record<string, unknown>)
            ? 'Builder preview: enter Preview Order Number and Preview Email on NavarTrackInfo, then refresh the preview.'
            : "We couldn't find tracking information for this order. Please check your link and try again.";
          blocks = injectOrderStatusIntoBlocks(blocks, {
            orderStatus: null,
            orderStatusError: orderStatusErrorProp,
          });
        } else {
          try {
            const orderStatusResult = await fetchOrderStatus({ orderNumber, token, email });
            if (isOrderStatusError(orderStatusResult)) {
              orderStatusErrorProp =
                orderStatusResult.error?.message ||
                (email
                  ? "We couldn't find an order matching that order number and email address. Please check and try again."
                  : "We couldn't find tracking information for this order. Please check your link and try again.");
              blocks = injectOrderStatusIntoBlocks(blocks, {
                orderStatus: null,
                orderStatusError: orderStatusErrorProp,
              });
            } else {
              // Never put the raw NS payload in pageProps (__NEXT_DATA__) — strip PII / unused fields
              orderStatusProp = toPublicOrderStatus(orderStatusResult);
              // Full/slim payload stays on page props / BuilderProvider only — do not put it in
              // block options (avoids duplicate POD base64 and stale props winning).
              blocks = injectOrderStatusIntoBlocks(blocks, {
                orderStatus: null,
                orderStatusError: null,
              });
            }
          } catch (orderStatusFetchError) {
            console.error('Failed to fetch order status from NetSuite', {
              orderNumber,
              hasToken: Boolean(token),
              hasEmail: Boolean(email),
              device,
              error:
                orderStatusFetchError instanceof Error
                  ? { name: orderStatusFetchError.name, message: orderStatusFetchError.message }
                  : orderStatusFetchError,
            });
            orderStatusErrorProp = 'Failed to load delivery information. Please try again later.';
            blocks = injectOrderStatusIntoBlocks(blocks, {
              orderStatus: null,
              orderStatusError: orderStatusErrorProp,
            });
          }
        }
      }

      page = {
        ...page,
        data: {
          ...page.data,
          blocks,
        },
      };
      // Inject LastUpdated as the 3rd block on Buying Guide pages when missing
      injectLastUpdatedBlock(page);
    }

    // Pre-render the nav so the client can mount it as a static island. Dynamic Yield edits
    // that DOM before hydration, and React must not reconcile it back afterwards.
    let navHtml = '';
    let mobileNavHtml = '';
    try {
      const promoBlock = data.slice(5).find((item: any) => item && (item.topPurpleBar || item.topNav));
      navHtml = renderNavHtml({
        navLinks: data[0] ?? [],
        mobileNavLinks: [],
        promo: promoBlock?.topNav ?? null,
      }).desktopHtml;
      mobileNavHtml = renderMobileNavShellHtml({ mobileNavLinks: data[1] ?? [] });
    } catch (navHtmlError) {
      console.error('Failed to pre-render top nav html', navHtmlError);
    }

    return {
      props: {
        page: page ?? null,
        data,
        showroomsWithIds,
        navHtml,
        mobileNavHtml,
        locale,
        // error: errorRecovery ?? null,
        staticProps: true,
        device,
        orderStatus: orderStatusProp,
        orderStatusError: orderStatusErrorProp,
      },
    };
  } catch (error) {
    return {
      props: {
        page: null,
        data: [],
        showroomCities: [],
        navHtml: '',
        mobileNavHtml: '',
        locale: '',
        device: 'desktop' as DeviceType,
        orderStatus: null,
        orderStatusError: null,
      },
    };
  }
}
